import { conform, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import { invariant } from '@epic-web/invariant'
import {
	type ActionFunctionArgs,
	json,
	type LoaderFunctionArgs,
	type MetaFunction,
	redirect,
} from '@remix-run/node'
import { Form, Link, useActionData, useLoaderData } from '@remix-run/react'
import * as React from 'react'
import { AuthenticityTokenInput } from 'remix-utils/csrf/react'
import {
	AuthFooter,
	FieldError,
	PasswordInput,
	PasswordMeter,
	QuoteCaption,
	SubmitButton,
} from '#app/components/auth/auth-ui.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { AuthShell } from '#app/components/layout/auth-shell.tsx'
import { FormError } from '#app/components/ui/field.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { WEDDING } from '#app/config/wedding.ts'
import { type VerifyFunctionArgs } from '#app/routes/_auth+/verify.tsx'
import { requireAnonymous, resetUserPassword } from '#app/utils/auth.server.ts'
import { validateCSRF } from '#app/utils/csrf.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { PasswordAndConfirmPasswordSchema } from '#app/utils/user-validation.ts'
import { verifySessionStorage } from '#app/utils/verification.server.ts'

const resetPasswordUsernameSessionKey = 'resetPasswordUsername'

export async function handleVerification({ submission }: VerifyFunctionArgs) {
	invariant(submission.value, 'submission.value should be defined by now')
	const target = submission.value.target
	const user = await prisma.user.findFirst({
		where: { OR: [{ email: target }, { email: target }] },
		select: { email: true },
	})
	// we don't want to say the user is not found if the email is not found
	// because that would allow an attacker to check if an email is registered
	if (!user) {
		submission.error.code = ['Invalid code']
		return json({ status: 'error', submission } as const, { status: 400 })
	}

	const verifySession = await verifySessionStorage.getSession()
	verifySession.set(resetPasswordUsernameSessionKey, user.email)
	return redirect('/reset-password', {
		headers: {
			'set-cookie': await verifySessionStorage.commitSession(verifySession),
		},
	})
}

const ResetPasswordSchema = PasswordAndConfirmPasswordSchema

async function requireResetPasswordUsername(request: Request) {
	await requireAnonymous(request)
	const verifySession = await verifySessionStorage.getSession(
		request.headers.get('cookie'),
	)
	const resetPasswordUsername = verifySession.get(
		resetPasswordUsernameSessionKey,
	)
	if (typeof resetPasswordUsername !== 'string' || !resetPasswordUsername) {
		throw redirect('/login')
	}
	return resetPasswordUsername
}

export async function loader({ request }: LoaderFunctionArgs) {
	const resetPasswordUsername = await requireResetPasswordUsername(request)
	return json({ resetPasswordUsername })
}

export async function action({ request }: ActionFunctionArgs) {
	const resetPasswordUsername = await requireResetPasswordUsername(request)
	const formData = await request.formData()
	await validateCSRF(formData, request.headers)
	const submission = parse(formData, {
		schema: ResetPasswordSchema,
	})
	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const)
	}
	if (!submission.value?.password) {
		return json({ status: 'error', submission } as const, { status: 400 })
	}
	const { password } = submission.value

	await resetUserPassword({ email: resetPasswordUsername, password })
	const verifySession = await verifySessionStorage.getSession()
	return redirectWithToast(
		'/login',
		{
			title: 'Password Reset',
			type: 'success',
			description: 'Sign in with your new password.',
		},
		{
			headers: {
				'set-cookie': await verifySessionStorage.destroySession(verifySession),
			},
		},
	)
}

export default function ResetPasswordPage() {
	const data = useLoaderData<typeof loader>()
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [password, setPassword] = React.useState('')

	const [form, fields] = useForm({
		id: 'reset-password',
		constraint: getFieldsetConstraint(ResetPasswordSchema),
		lastSubmission: actionData?.submission,
		onValidate({ formData }) {
			return parse(formData, { schema: ResetPasswordSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<AuthShell
			image="/img/demo/couple/field-embrace.webp"
			imageAlt={`${WEDDING.couple.names.join(' and ')} embracing in a golden field`}
			caption={<QuoteCaption quote={`“${WEDDING.scripture.text}”`} cite={WEDDING.scripture.cite} />}
			topRight={<Link to="/login" className="link">Sign in</Link>}
			footer={<AuthFooter />}
		>
			<p className="mono text-stone">Password recovery</p>
			<h1 className="mt-4 font-serif text-[60px] leading-[.95] tracking-[-.01em]">
				A new <em className="italic text-brown">password</em>
			</h1>
			<p className="mt-3 text-base text-stone">
				For <span className="text-ink">{data.resetPasswordUsername}</span>
			</p>

			<Form method="POST" {...form.props} className="mt-7 flex flex-col gap-5">
				<AuthenticityTokenInput />
				<div className="flex flex-col gap-2">
					<Label htmlFor={fields.password.id}>New password</Label>
					<PasswordInput
						{...conform.input(fields.password, { type: 'password' })}
						autoComplete="new-password"
						autoFocus
						onInput={e => setPassword(e.currentTarget.value)}
					/>
					<PasswordMeter value={password} />
					<FieldError errors={fields.password.errors} />
				</div>
				<div className="flex flex-col gap-2">
					<Label htmlFor={fields.confirmPassword.id}>Confirm password</Label>
					<PasswordInput
						{...conform.input(fields.confirmPassword, { type: 'password' })}
						autoComplete="new-password"
					/>
					<FieldError errors={fields.confirmPassword.errors} />
				</div>
				<FormError>{form.errors?.[0]}</FormError>
				<SubmitButton pending={isPending}>Reset password</SubmitButton>
			</Form>
		</AuthShell>
	)
}

export const meta: MetaFunction = () => [{ title: `Reset password — ${WEDDING.title}` }]

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
